import { defineStore } from "pinia";
import { ref } from "vue";
import { api, getToken, setToken } from "../api";
import { readableError } from "../composables/useFormat";
import { clearOfflineSnapshots } from "../offlineSnapshot";
import type { User } from "../types";

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const notice = ref("");
  const booted = ref(false);

  async function boot() {
    if (booted.value) return;
    booted.value = true;
    if (!getToken()) return;
    try {
      const response = await api.me();
      user.value = response.user;
      notice.value = "";
    } catch (error) {
      setToken(null);
      user.value = null;
      notice.value = readableError(error);
    }
  }

  async function login(email: string, password: string) {
    const response = await api.login(email, password);
    setToken(response.token);
    user.value = response.user;
    notice.value = "";
    return response.user;
  }

  async function register(email: string, password: string) {
    const response = await api.register(email, password);
    setToken(response.token);
    user.value = response.user;
    notice.value = "";
    return response.user;
  }

  function logout() {
    if (user.value?.id) clearOfflineSnapshots(user.value.id).catch(() => undefined);
    setToken(null);
    user.value = null;
    notice.value = "";
  }

  async function updateSettings(payload: { ai_enabled?: boolean; onboarding_version?: number; schedule_default_duration_minutes?: number; startup_channel_mode?: User["startup_channel_mode"]; default_channel_id?: string | null; time_zone?: string }) {
    const response = await api.settings(payload);
    user.value = response.user;
    return response.user;
  }

  return {
    user,
    notice,
    boot,
    login,
    register,
    logout,
    updateSettings
  };
});
